// Textos dos Tentos: cada movimento do extrato vira título, detalhe e cor; os dias viram Hoje, Ontem ou a data.
import { NOME_DO_PLANO, ehPlano, pacoteDe } from '@truco6/planos';
import { type ItemExtrato, NOME_DO_METODO, reais } from './api.ts';

export type IconeMovimento = 'compra' | 'plano' | 'premio' | 'ingresso' | 'devolvido' | 'ajuste' | 'reservado';
export type CorMovimento = 'entra' | 'premio' | 'sai' | 'neutro';

export interface Linha {
  titulo: string;
  detalhe: string;
  icone: IconeMovimento;
  cor: CorMovimento;
  plano?: string; // crédito do plano: a cor do selo
}

const FUSO = 'America/Sao_Paulo';
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const texto = (x: unknown) => (typeof x === 'string' && x.trim() ? x.trim() : null);
const numero = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : null);

// Nome da sala no extrato: o nome que o dono deu ou o código.
function nomeDaSala(d: Record<string, unknown>): string {
  return texto(d.nome) ?? (texto(d.codigo) ? `Sala ${d.codigo as string}` : 'Sala valendo Tentos');
}

// Quantidade com sinal: +10, −6 (sinal de menos de verdade).
export function comSinal(n: number): string {
  return n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '0';
}

export function descrever(m: ItemExtrato): Linha {
  const d = m.detalhes ?? {};
  switch (m.tipo) {
    case 'compra': {
      const metodo = d.metodo === 'pix' || d.metodo === 'cartao' ? NOME_DO_METODO[d.metodo] : null;
      const centavos = numero(d.centavos) ?? pacoteDe(d.pacote)?.centavos ?? null;
      return { titulo: `Compra · ${m.quantidade} Tentos`, detalhe: [metodo, centavos !== null ? reais(centavos) : null].filter(Boolean).join(' · '), icone: 'compra', cor: 'entra' };
    }
    case 'compra_reembolsada': {
      const centavos = numero(d.centavos);
      const detalhe = d.motivo === 'contestacao' ? 'Contestação no cartão'
        : d.motivo === 'desistencia' ? `Desistência${centavos !== null ? ` · ${reais(centavos)} de volta` : ''}` : 'Compra reembolsada';
      return { titulo: `Compra devolvida · ${-m.quantidade} Tentos`, detalhe, icone: 'devolvido', cor: 'sai' };
    }
    case 'credito_plano': {
      const plano = ehPlano(d.plano) ? d.plano : null, mes = texto(d.mes);
      if (m.quantidade < 0) return { titulo: 'Crédito do plano retirado', detalhe: 'Desistência da assinatura', icone: 'plano', cor: 'sai', plano: plano ?? undefined };
      const nomeMes = mes ? MESES[Number(mes.slice(5, 7)) - 1] : null;
      if (texto(d.subida) && plano) {
        return { titulo: `Subida para o ${NOME_DO_PLANO[plano]}`, detalhe: nomeMes ? `Diferença do mês de ${nomeMes}` : 'Diferença do mês', icone: 'plano', cor: 'entra', plano };
      }
      return {
        titulo: plano ? `Crédito do plano ${NOME_DO_PLANO[plano]}` : 'Crédito do plano', detalhe: nomeMes ? `Mês de ${nomeMes} pago` : 'Mês pago',
        icone: 'plano', cor: 'entra', plano: plano ?? undefined,
      };
    }
    case 'ingresso_debitado':
      return { titulo: 'Ingresso', detalhe: nomeDaSala(d), icone: 'ingresso', cor: 'sai' };
    case 'ingresso_devolvido':
      return { titulo: 'Ingresso devolvido', detalhe: `${nomeDaSala(d)} · partida anulada`, icone: 'devolvido', cor: 'entra' };
    case 'premio':
      return { titulo: 'Prêmio', detalhe: `${nomeDaSala(d)} · Nós ganhamos`, icone: 'premio', cor: 'premio' };
    case 'credito_admin':
      return { titulo: 'Crédito do Truco de 6', detalhe: 'Ajuste do suporte', icone: 'ajuste', cor: 'entra' };
    case 'debito_admin':
      return { titulo: 'Ajuste do suporte', detalhe: 'Débito feito pelo Truco de 6', icone: 'ajuste', cor: 'sai' };
    case 'contestacao_revertida':
      return { titulo: `Contestação encerrada · ${m.quantidade} Tentos`, detalhe: 'Tentos devolvidos', icone: 'devolvido', cor: 'entra' };
    case 'cosmetico':
      return { titulo: 'Cosmético', detalhe: texto(d.nome) ?? 'Item da coleção', icone: 'compra', cor: 'sai' };
    case 'recompensa':
      return { titulo: 'Recompensa', detalhe: MOTIVO_DA_RECOMPENSA[texto(d.motivo) ?? ''] ?? 'Recompensa do jogo', icone: 'premio', cor: 'premio' };
  }
}

// De onde veio a recompensa em Tentos (packages/gamificacao, ACOES).
const MOTIVO_DA_RECOMPENSA: Record<string, string> = {
  ofensiva_7: 'Ofensiva de 7 dias', ofensiva_30: 'Ofensiva de 30 dias', ofensiva_100: 'Ofensiva de 100 dias', ofensiva_365: 'Ofensiva de 365 dias',
  convite_partidas: 'Amigo convidado jogou 3 partidas', convite_chegou: 'Boas-vindas pelo convite', convite_assinou: 'Amigo convidado assinou um plano',
  bau_7: 'Baú do dia 7', passe: 'Passe da Temporada',
};

// Chave do dia em São Paulo (2026-10-16).
export function diaDe(ms: number): string {
  return new Date(ms).toLocaleDateString('en-CA', { timeZone: FUSO });
}

// Rótulo do grupo do extrato: HOJE, ONTEM ou 14 DE OUTUBRO.
export function rotuloDoDia(ms: number, agora: number): string {
  const dia = diaDe(ms);
  if (dia === diaDe(agora)) return 'Hoje';
  if (dia === diaDe(agora - 86_400_000)) return 'Ontem';
  const [ano, mes, d] = dia.split('-').map(Number) as [number, number, number];
  return `${d} de ${MESES[mes - 1]}` + (ano !== Number(diaDe(agora).slice(0, 4)) ? ` de ${ano}` : '');
}

// Agrupa por dia, na ordem em que chegam (mais novos primeiro).
export function porDia<T>(itens: T[], em: (x: T) => number): { dia: string; itens: T[] }[] {
  const grupos: { dia: string; itens: T[] }[] = [];
  for (const x of itens) {
    const dia = diaDe(em(x));
    const ultimo = grupos.at(-1);
    if (ultimo?.dia === dia) ultimo.itens.push(x); else grupos.push({ dia, itens: [x] });
  }
  return grupos;
}

// Código da sala a partir do id da reserva (CODIGO-criadaEm).
export const codigoDaReserva = (sala: string) => sala.split('-')[0] ?? sala;
